import type { DraftNotebookKind } from './draftNotebookKind';

function section(heading: string, content: string): string {
  const c = content.trim();
  if (!c) return '';
  return `## ${heading}\n\n${c}\n\n`;
}

function extractSection(markdown: string, heading: string): string {
  const re = new RegExp(
    `^##\\s+${heading.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*\\n([\\s\\S]*?)(?=^##\\s+|\\n?$)`,
    'm'
  );
  const m = markdown.match(re);
  return m ? m[1].trim() : '';
}

export type RecipeDraftFields = {
  servingsTiming: string;
  ingredients: string;
  instructions: string;
  notes: string;
};

export function composeRecipeMarkdown(fields: RecipeDraftFields): string {
  return [
    section('Servings & timing', fields.servingsTiming),
    section('Ingredients', fields.ingredients),
    section('Instructions', fields.instructions),
    section('Chef notes', fields.notes),
  ]
    .join('')
    .trim();
}

export function parseRecipeMarkdown(markdown: string): RecipeDraftFields {
  return {
    servingsTiming: extractSection(markdown, 'Servings & timing'),
    ingredients: extractSection(markdown, 'Ingredients'),
    instructions: extractSection(markdown, 'Instructions'),
    notes: extractSection(markdown, 'Chef notes'),
  };
}

export type MovieDraftFields = {
  deck: string;
  review: string;
  rating: string;
  whereToWatch: string;
};

export function composeMovieMarkdown(fields: MovieDraftFields): string {
  return [
    section('Deck / showtimes', fields.deck),
    section('Review', fields.review),
    section('Rating', fields.rating),
    section('Where to watch', fields.whereToWatch),
  ]
    .join('')
    .trim();
}

export function parseMovieMarkdown(markdown: string): MovieDraftFields {
  return {
    deck: extractSection(markdown, 'Deck / showtimes'),
    review: extractSection(markdown, 'Review'),
    rating: extractSection(markdown, 'Rating'),
    whereToWatch: extractSection(markdown, 'Where to watch'),
  };
}

export type EventDraftFields = {
  whenWhere: string;
  tickets: string;
  details: string;
};

export function composeEventMarkdown(fields: EventDraftFields): string {
  return [
    section('When & where', fields.whenWhere),
    section('Tickets & links', fields.tickets),
    section('Details', fields.details),
  ]
    .join('')
    .trim();
}

export function parseEventMarkdown(markdown: string): EventDraftFields {
  return {
    whenWhere: extractSection(markdown, 'When & where'),
    tickets: extractSection(markdown, 'Tickets & links'),
    details: extractSection(markdown, 'Details'),
  };
}

export function emptyStructuredBody(kind: DraftNotebookKind): string {
  switch (kind) {
    case 'recipe':
      return composeRecipeMarkdown({
        servingsTiming: '',
        ingredients: '',
        instructions: '',
        notes: '',
      });
    case 'movie':
      return composeMovieMarkdown({ deck: '', review: '', rating: '', whereToWatch: '' });
    case 'event':
      return composeEventMarkdown({ whenWhere: '', tickets: '', details: '' });
    default:
      return '';
  }
}
